/**
 * The blackjack room's money, written in Buster Bucks.
 *
 * ========================== WHY THIS FILE EXISTS AT ALL ========================
 * `engine/blackjack/money.ts` has printed `$400` and `$25` since the room was built,
 * and its reasoning was sound at the time: a player told a decision cost eleven fifty
 * out of the four hundred in front of them has felt it, where a player told it cost
 * 0.46 units has learned an abstraction. The dollars were never a claim that anything
 * was worth anything, and the page said so in those words.
 *
 * Phase 4 makes the claim untrue in a way copy cannot fix. The rack is now bought from
 * the wallet: it is 400 BUCKS, it comes out of a balance, and it goes back in when the
 * player stands up. A `$` beside a number that moves a balance is telling somebody
 * their balance is money.
 *
 * The engine is not this phase's to touch — standing rule 3 — and it should not be
 * anyway: `CHIP_VALUE`, `TABLE_MINIMUM` and `BUY_IN` are numbers about a table, and
 * they are correct. What was wrong was the FORMATTING, which is a UI concern that had
 * ended up in the engine. So the numbers still come from there and the words come from
 * here, and the room imports these under the old names so that eleven hundred lines of
 * call sites did not have to be rewritten to move a dollar sign.
 *
 * `engine/blackjack/money.ts`'s own `money()` and `signedMoney()` are now unused by the
 * app. Deleting them is an engine change and belongs in Phase 8, which re-bases this
 * room on the kit; `tests/blackjack-money.test.ts` still covers them meanwhile.
 * ===============================================================================
 */

/**
 * Bucks, written the way the table writes them.
 *
 * Whole numbers get no decimals — a stack reads `400`, not `400.00`. A natural pays
 * three to two, so halves are real and do get them. Grouped above a thousand, because
 * a rack and a half of green chips is four figures and "1250" is not a number anybody
 * reads at a glance.
 */
export function bucks(amount: number): string {
  if (!Number.isFinite(amount)) return "—";
  const neg = amount < 0;
  const v = Math.abs(amount);
  const body = Number.isInteger(v)
    ? v.toLocaleString("en-US", { maximumFractionDigits: 0 })
    : v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${neg ? "−" : ""}${body}`;
}

/** The same, always signed — for a number that is a change rather than a total. */
export function signedBucks(amount: number): string {
  if (!Number.isFinite(amount)) return "—";
  if (amount === 0) return "0";
  return `${amount > 0 ? "+" : "−"}${bucks(Math.abs(amount))}`;
}
